import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Building2, FileEdit, CalendarClock } from 'lucide-react';
import api from '../services/apiClient';
import useAuthStore from '../store/authStore';
import { toast } from '../components/toast';
import CompanyLogo from '../components/CompanyLogo';

export default function Dashboard() {
  const switchCompany = useAuthStore((s) => s.switchCompany);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setData(await api.get('/analytics/overview'));
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const goToCompany = (id, path) => {
    switchCompany(id);
    navigate(path);
  };

  if (loading) return <p className="muted">Carregando…</p>;
  if (!data) return null;

  const totals = data.totals || { companies: 0, pendingApproval: 0, withGap: 0 };
  const companies = data.companies || [];
  const gapDays = data.gapDays ?? 7;

  return (
    <div>
      <div className="grid cols-3 mb16">
        <Stat icon={Building2} label="Empresas" value={totals.companies ?? 0} />
        <Stat icon={FileEdit} label="Rascunhos pendentes" value={totals.pendingApproval ?? 0} />
        <Stat icon={AlertTriangle} label={`Sem conteúdo (${gapDays}d)`} value={totals.withGap ?? 0}
          danger={(totals.withGap ?? 0) > 0} />
      </div>

      {companies.length === 0 && (
        <div className="card center muted" style={{ padding: 40 }}>
          Nenhuma empresa ainda. Crie uma em "Empresas".
        </div>
      )}

      <div className="dash-grid">
        {companies.map((c) => (
          <div key={c.id} className="card dash-card">
            <div className="dash-head">
              <CompanyLogo url={c.logoUrl} name={c.name} size={34} />
              <strong className="dash-name" title={c.name}>{c.name}</strong>
              {c.hasContentGap
                ? <span className="badge red dash-tag"><AlertTriangle size={12} /> Sem agenda</span>
                : <span className="badge green dash-tag"><CheckCircle2 size={12} /> Em dia</span>}
            </div>

            <div className="dash-stats">
              <div className="ds"><b>{c.counts.draft}</b><span>rascunhos</span></div>
              <div className="ds"><b>{c.counts.approved}</b><span>aprovados</span></div>
              <div className="ds"><b>{c.counts.scheduled}</b><span>agendados</span></div>
            </div>

            <p className="dash-next">
              {c.nextScheduledAt
                ? <><CalendarClock size={13} /> Próximo: {new Date(c.nextScheduledAt).toLocaleString('pt-BR')}</>
                : <>Nenhum post agendado</>}
            </p>

            <div className="dash-actions">
              <button className="btn sm" onClick={() => goToCompany(c.id, '/gerar')}>Gerar conteúdo</button>
              <button className="btn sm secondary" onClick={() => goToCompany(c.id, '/calendario')}>Ver calendário</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, danger }) {
  return (
    <div className="card stat-card">
      <div className="stat-ico" style={{
        background: danger ? '#fee2e2' : 'var(--primary-50)', color: danger ? 'var(--danger)' : 'var(--primary)'
      }}>
        <Icon size={22} />
      </div>
      <div>
        <div className="stat-num">{value}</div>
        <div className="muted">{label}</div>
      </div>
    </div>
  );
}
